"use client";

/* ————— Scrub primitives (Jay direction, 2026-07-10) —————
   Every reveal on the homepage is LINKED TO SCROLL POSITION: move the
   page 5% and the transition moves 5%, in either direction. That means
   GSAP ScrollTrigger `scrub` — NOT Framer Motion useScroll (stalls on
   iOS Safari, v2 postmortem) and NOT time-based whileInView.

   <Scrub>      — wrapper that fades/slides its content through an
                  entry window as it scrolls into view.
   <ScrubWords> — the app's WordStream reveal (per-word opacity cascade,
                  left→right — see mobile/app/onboarding/components/
                  WordStream.tsx), mapped onto scroll progress.

   Both render fully visible by default (no-JS / SEO / reduced-motion)
   and only hide+animate once GSAP mounts. Only opacity/transform are
   animated. A small `scrub` smoothing (0.4–0.6) keeps it silky without
   decoupling from the scrollbar.
*/

import React, { useLayoutEffect, useMemo, useRef } from "react";

type Dir = "up" | "left" | "right" | "none";

const OFFSET: Record<Dir, { x: number; y: number }> = {
  up: { x: 0, y: 34 },
  left: { x: -44, y: 0 },
  right: { x: 44, y: 0 },
  none: { x: 0, y: 0 },
};

function usePrefersReducedMotion() {
  // read once on mount inside effects; SSR-safe helper
  return () =>
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function Scrub({
  children,
  from = "up",
  className,
  start = "top 92%",
  end = "top 55%",
  as: Tag = "div",
}: {
  children: React.ReactNode;
  from?: Dir;
  className?: string;
  /** ScrollTrigger window — the animation's 0% and 100% scroll positions */
  start?: string;
  end?: string;
  as?: "div" | "figure" | "section" | "span";
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();

  useLayoutEffect(() => {
    if (reduced()) return;
    let ctx: { revert: () => void } | undefined;
    let cancelled = false;
    (async () => {
      const [{ gsap }, { ScrollTrigger }] = await Promise.all([
        import("gsap"),
        import("gsap/ScrollTrigger"),
      ]);
      if (cancelled || !ref.current) return;
      gsap.registerPlugin(ScrollTrigger);
      const { x, y } = OFFSET[from];
      ctx = gsap.context(() => {
        gsap.fromTo(
          ref.current,
          { autoAlpha: 0, x, y },
          {
            autoAlpha: 1,
            x: 0,
            y: 0,
            ease: "none",
            scrollTrigger: { trigger: ref.current, start, end, scrub: 0.5 },
          }
        );
      }, ref);
    })();
    return () => {
      cancelled = true;
      ctx?.revert();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Comp = Tag as React.ElementType;
  return (
    <Comp ref={ref} className={className}>
      {children}
    </Comp>
  );
}

/** Split text into word spans (whitespace preserved with its word). */
function words(text: string): string[] {
  return text.match(/\S+\s*/g) ?? [text];
}

export function ScrubWords({
  text,
  className,
  style,
  start = "top 88%",
  end = "top 45%",
}: {
  text: string;
  className?: string;
  style?: React.CSSProperties;
  start?: string;
  end?: string;
}) {
  const ref = useRef<HTMLParagraphElement>(null);
  const reduced = usePrefersReducedMotion();
  const tokens = useMemo(() => words(text), [text]);

  useLayoutEffect(() => {
    if (reduced()) return;
    let ctx: { revert: () => void } | undefined;
    let cancelled = false;
    (async () => {
      const [{ gsap }, { ScrollTrigger }] = await Promise.all([
        import("gsap"),
        import("gsap/ScrollTrigger"),
      ]);
      if (cancelled || !ref.current) return;
      gsap.registerPlugin(ScrollTrigger);
      const spans = ref.current.querySelectorAll(".js-w");
      ctx = gsap.context(() => {
        // WordStream, scroll-linked: the cascade position IS the scroll
        // position. Each word occupies a slice of the window; a light
        // overlap keeps it reading as a sweep, not a ticker.
        gsap.fromTo(
          spans,
          { opacity: 0.06 },
          {
            opacity: 1,
            ease: "none",
            stagger: { each: 0.6 / Math.max(spans.length, 1), from: "start" },
            duration: 0.4,
            scrollTrigger: { trigger: ref.current, start, end, scrub: 0.4 },
          }
        );
      }, ref);
    })();
    return () => {
      cancelled = true;
      ctx?.revert();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <p ref={ref} className={className} style={style}>
      {tokens.map((w, i) => (
        <span key={i} className="js-w">
          {w}
        </span>
      ))}
    </p>
  );
}
